import { Checkbox, FontIcon, Icon, Label, Text } from '@fluentui/react';
import { keyPress } from 'app/components/helpers';
import useStore, { IStore } from 'app/store';
import { StringConstants } from 'app/utils/constants';

import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { IOptionProps } from '../../models';
import { IStateData } from 'app/models/common/post';

interface IProps {
    selectedKeys: IOptionProps[];
    setSelectedKeys: Function;
    options?: IOptionProps[];
    stateData?: IStateData;
    setFilterActive: (filterActive: boolean) => void;
}

export const AgingFilter: React.FC<IProps> = ({ selectedKeys, setSelectedKeys, options, stateData, setFilterActive }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const filterPanelDisabled = useStore((state: IStore) => state.filterPanelDisabled);

    const setQueryStateData = (data: IStateData) => {
        setSelectedKeys(data.agingSelection);
    };

    const onChange = (key: string) => {
        if (key === StringConstants.ALL) {
            if (selectedKeys.filter((item) => item.key === key).length === 0) {
                let arr: IOptionProps[] = [];
                options.forEach((ele) => arr.push(ele));
                setQueryStateData({ agingSelection: arr });
            } else {
                setSelectedKeys([]);
            }
        } else {
            if (selectedKeys.filter((item) => item.key === key).length === 0) {
                const tempSelectedKeys = [...selectedKeys, options.filter((item) => item.key === key)[0]];
                setQueryStateData({ agingSelection: tempSelectedKeys });
            } else {
                const tempSelectedKeys = selectedKeys.filter((item) => item.key !== key && item.key !== StringConstants.ALL);
                setQueryStateData({ agingSelection: tempSelectedKeys });
            }
        }
    };
    const isChecked = (ele: string) => {
        return selectedKeys.filter((item) => item.key === ele).length !== 0;
    };
    const getLabel = () => {
        if (selectedKeys?.length === 0 || selectedKeys?.filter((ele) => ele.key === StringConstants.ALL).length !== 0) {
            return StringConstants.UPPERCASE_ALL;
        } else {
            let arr: string[] = [];
            selectedKeys?.forEach((item) => {
                arr.push(item.text);
            });
            return arr.join(', ');
        }
    };
    const onToggleShow = () => {
        if (!filterPanelDisabled) {
            setShowDropdown((value) => !value);
        }
    };
    const dropDownRef = useRef<HTMLDivElement>(null);
    useDropDownDismiss(dropDownRef, onToggleShow, filterPanelDisabled);

    const checkEquality = (arr1: IOptionProps[], arr2: IOptionProps[]) => {
        if (arr1?.length !== arr2?.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr2.filter((it) => it.key === arr1[i].key).length === 0) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        if (showDropdown && selectedKeys.length === 0) {
            setSelectedKeys(options);
        }
    }, [showDropdown]);
    useEffect(() => {
        if (
            options.length !== 0 &&
            selectedKeys?.length !== 0 &&
            checkEquality(
                selectedKeys,
                options.filter((it) => it.key !== StringConstants.ALL)
            )
        ) {
            setSelectedKeys(options.slice(0));
        }
    }, [selectedKeys, options]);

    useEffect(() => {
        if (selectedKeys.length > 0 && selectedKeys.length !== options.length) {
            setFilterActive(true);
        } else {
            setFilterActive(false);
        }
    }, [selectedKeys]);

    return (
        <div className="aging-filter filter-cont">
            <Text className="filter-heading">Aging {(selectedKeys.length > 0 && selectedKeys.length !== options.length) && <FontIcon iconName="LocationDot" className="filter-active-icon" />}</Text>

            <div
                className={`placeholder-title row-flex-box align-center ${filterPanelDisabled ? 'filter-disabled' : ''}`}
                tabIndex={0}
                data-testid="aging-filter-open-btn-test"
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    keyPress(e, onToggleShow);
                }}
                onClick={() => onToggleShow()}>
                <Text className="placeholder-label">{getLabel()}</Text> <Icon iconName="ChevronDown" />
            </div>
            {showDropdown && (
                <div ref={dropDownRef} className="dropdown-cont">
                    <div
                        tabIndex={0}
                        data-testid="aging-filter-close-btn-test"
                        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                            keyPress(e, onToggleShow);
                        }}
                        onClick={() => onToggleShow()}
                        className="row-flex-box align-center dropdown-header">
                        <Text>Aging</Text>
                        <Icon iconName="ChevronUp" />
                    </div>
                    {options.map((item, index) => (
                        <div
                            key={index}
                            data-testid={`dropdown-item-test-${index}`}
                            tabIndex={0}
                            onKeyPress={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                    onChange(item.key as string);
                                }
                            }}
                            className={`dropdown-item row-flex-box align-center ${
                                isChecked(item.key as string) ? 'selected' : ''
                            } `}>
                            <Checkbox
                                data-testid={`dropdown-checkbox-test-${index}`}
                                checked={isChecked(item.key as string)}
                                onChange={() => onChange(item.key as string)}
                            />
                            <span className={`aging-icon ${'icon-' + item.key}`}></span>
                            <Label>{item.text}</Label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
const useDropDownDismiss = (ref: MutableRefObject<HTMLDivElement>, onToggleShow: Function, filterPanelDisabled: boolean) => {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onToggleShow();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, filterPanelDisabled]);
};
